import confetti from 'canvas-confetti';
import { GameState, Player, StakesLevel, GameLogEntry } from '../engine/types';
import { getBinaryBits, canCallBitIt, getStakesWinPoints } from '../engine/game';
import { t, TranslationKey } from '../i18n';
import { playBitIt, playDoubleBitIt, playFold, playMatchWin } from '../audio';

export interface HudCallbacks {
  onCallBitIt: () => void;
  onRespondChallenge: (response: 'face' | 'fold') => void;
  onSurrender: () => void;
  onPlayAgain: () => void;
  onBackToLobby: () => void;
  onSendEmote: (text: string) => void;
}

export class Hud {
  private container: HTMLElement;
  private callbacks: HudCallbacks;
  private localPlayerId: string = '';
  private emotesOpen: boolean = false;
  private hasCelebratedMatch: boolean = false;
  private hintDismissed: boolean = false;

  constructor(container: HTMLElement, callbacks: HudCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
    try {
      this.hintDismissed = localStorage.getItem('slimelings_dismiss_hint') === 'true';
    } catch {
      this.hintDismissed = false;
    }
  }

  public setLocalPlayer(playerId: string): void {
    this.localPlayerId = playerId;
  }

  public update(state: GameState): void {
    this.container.innerHTML = '';

    const overlay = document.createElement('div');
    overlay.className = 'hud-overlay';

    // 1. Top Section: Scorecards & Game Status
    const topBar = document.createElement('div');
    topBar.className = 'hud-top-bar';

    // Top status badge (Round, Trick, Stakes)
    const statusBadge = document.createElement('div');
    statusBadge.className = 'match-status-badge';

    const roundText = t('round_num', { num: state.roundNumber });
    const trickText = t('trick_num', { num: state.currentTrick.trickNumber });
    const stakesText = this.getStakesLabel(state.challenge.level);
    const isBitIt = state.challenge.level !== 'normal';

    statusBadge.innerHTML = `
      <span class="status-round-info">${roundText} • ${trickText}</span>
      <span class="stakes-badge ${isBitIt ? 'bit-it' : ''}">${stakesText}</span>
    `;

    topBar.appendChild(statusBadge);
    overlay.appendChild(topBar);

    // 2. Scoreboard Seats (Sul, Norte, Leste, Oeste)
    const seatsContainer = document.createElement('div');
    seatsContainer.className = 'hud-seats-container';

    if (state.mode === 'duel') {
      const opp = state.players.find((p) => p.id !== this.localPlayerId);
      const local = state.players.find((p) => p.id === this.localPlayerId);

      if (opp) {
        const oppCard = this.createPlayerScorecard(opp, state, 'seat-north');
        seatsContainer.appendChild(oppCard);
      }
      if (local) {
        const localCard = this.createPlayerScorecard(local, state, 'seat-south');
        seatsContainer.appendChild(localCard);
      }
    } else {
      // 4-Player / Teams
      const localIdx = state.players.findIndex((p) => p.id === this.localPlayerId);
      const n = state.players.length;

      state.players.forEach((p, idx) => {
        let seatClass = 'seat-south';
        if (idx === localIdx) {
          seatClass = 'seat-south';
        } else if (idx === (localIdx + 1) % n) {
          seatClass = 'seat-west';
        } else if (idx === (localIdx + 2) % n) {
          seatClass = 'seat-north';
        } else {
          seatClass = 'seat-east';
        }
        seatsContainer.appendChild(this.createPlayerScorecard(p, state, seatClass));
      });
    }

    overlay.appendChild(seatsContainer);

    // 3. Center Event Banner (Last game log)
    if (state.logs.length > 0) {
      const lastLog = state.logs[state.logs.length - 1];
      const banner = document.createElement('div');
      banner.className = 'event-banner';
      banner.textContent = this.formatLogEntry(lastLog);
      overlay.appendChild(banner);
    }

    // 4. Action Area: Positioned clearly ABOVE the hand cards
    const actionArea = document.createElement('div');
    actionArea.className = 'hud-action-area';

    const localPlayer = state.players.find((p) => p.id === this.localPlayerId);
    const isMyTurn =
      state.status === 'playing' &&
      state.currentTurnPlayerId === this.localPlayerId &&
      state.challenge.status !== 'pending' &&
      localPlayer &&
      !localPlayer.folded;

    const hasPendingChallengeForMe =
      state.challenge.status === 'pending' &&
      state.challenge.responses[this.localPlayerId] === 'pending';

    if (hasPendingChallengeForMe) {
      // Prompt challenge response: Face vs Fold vs Double
      const caller = state.players.find((p) => p.id === state.challenge.callerId);
      const challengePrompt = document.createElement('div');
      challengePrompt.className = 'turn-prompt challenge-alert';
      challengePrompt.textContent = t('event_bit_it_called', {
        name: caller?.name || t('player_opponent'),
        stakes: state.challenge.level === 'bit_it' ? 4 : 8,
      });
      actionArea.appendChild(challengePrompt);

      const buttonsRow = document.createElement('div');
      buttonsRow.className = 'action-buttons-row';

      const faceBtn = document.createElement('button');
      faceBtn.className = 'btn-action btn-face';
      faceBtn.textContent = t('action_face');
      faceBtn.onclick = () => {
        playBitIt();
        this.callbacks.onRespondChallenge('face');
      };
      buttonsRow.appendChild(faceBtn);

      const foldBtn = document.createElement('button');
      foldBtn.className = 'btn-action btn-fold';
      foldBtn.textContent = t('action_fold');
      foldBtn.onclick = () => {
        playFold();
        this.callbacks.onRespondChallenge('fold');
      };
      buttonsRow.appendChild(foldBtn);

      // Check if re-raise to Double Bit-It is allowed
      if (canCallBitIt(state, this.localPlayerId)) {
        const doubleBtn = document.createElement('button');
        doubleBtn.className = 'btn-action btn-double-bit-it';
        doubleBtn.textContent = t('action_double_bit_it');
        doubleBtn.onclick = () => {
          playDoubleBitIt();
          this.callbacks.onCallBitIt();
        };
        buttonsRow.appendChild(doubleBtn);
      }

      actionArea.appendChild(buttonsRow);
    } else {
      // Normal turn actions
      if (isMyTurn) {
        const yourTurnPrompt = document.createElement('div');
        yourTurnPrompt.className = 'turn-prompt my-turn-prompt';
        yourTurnPrompt.textContent = t('your_turn');
        actionArea.appendChild(yourTurnPrompt);

        // Dismissible instruction chip
        if (!this.hintDismissed) {
          const hintChip = document.createElement('div');
          hintChip.className = 'turn-hint-chip';
          hintChip.innerHTML = `
            <span>${t('action_drag_or_tap')}</span>
            <button class="btn-close-hint" title="${t('btn_dismiss_hint')}">✕</button>
          `;
          hintChip.querySelector('.btn-close-hint')?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.hintDismissed = true;
            try {
              localStorage.setItem('slimelings_dismiss_hint', 'true');
            } catch {
              // ignore
            }
            hintChip.remove();
          });
          actionArea.appendChild(hintChip);
        }

        const buttonsRow = document.createElement('div');
        buttonsRow.className = 'action-buttons-row';

        if (canCallBitIt(state, this.localPlayerId)) {
          const bitItBtn = document.createElement('button');
          bitItBtn.className = 'btn-action btn-bit-it';
          bitItBtn.textContent = t('action_bit_it');
          bitItBtn.onclick = () => {
            playBitIt();
            this.callbacks.onCallBitIt();
          };
          buttonsRow.appendChild(bitItBtn);
        }

        const foldTurnBtn = document.createElement('button');
        foldTurnBtn.className = 'btn-action btn-fold';
        foldTurnBtn.textContent = t('action_fold');
        foldTurnBtn.onclick = () => {
          playFold();
          this.callbacks.onSurrender();
        };
        buttonsRow.appendChild(foldTurnBtn);

        actionArea.appendChild(buttonsRow);
      } else if (state.status === 'playing') {
        const waitingPrompt = document.createElement('div');
        waitingPrompt.className = 'turn-prompt waiting-prompt';

        if (state.challenge.status === 'pending') {
          // A challenge is currently pending response from opponent
          waitingPrompt.textContent = t('waiting_for_challenge_response');
        } else {
          const currentTurnPlayer = state.players.find((p) => p.id === state.currentTurnPlayerId);
          waitingPrompt.textContent = t('waiting_for_player', { name: currentTurnPlayer?.name || '...' });
        }
        actionArea.appendChild(waitingPrompt);
      }
    }

    overlay.appendChild(actionArea);

    // 5. Emotes Bar
    const emotesContainer = this.createEmotesBar();
    overlay.appendChild(emotesContainer);

    this.container.appendChild(overlay);

    if (state.status !== 'match_end') {
      this.hasCelebratedMatch = false;
    }
  }

  private createPlayerScorecard(player: Player, state: GameState, seatClass: string): HTMLElement {
    const card = document.createElement('div');
    const isCurrentTurn = state.currentTurnPlayerId === player.id && state.status === 'playing';
    card.className = `scoreboard-card ${seatClass} ${isCurrentTurn ? 'active-turn' : ''} ${player.folded ? 'folded' : ''}`;

    const bits = getBinaryBits(player.score);
    const bitValues = [1, 2, 4, 8];

    const isLocal = player.id === this.localPlayerId;
    const nameLabel = isLocal ? `${player.name} (${t('player_you')})` : player.name;

    card.innerHTML = `
      <div class="player-info">
        <span class="player-name" title="${nameLabel}">${nameLabel}</span>
        <span class="player-pts">${player.score} / 16</span>
      </div>
      <div class="gems-row">
        ${bitValues
          .map(
            (val, idx) => `
          <div class="gem-point ${bits[idx] ? 'lit' : ''}" title="${t('card_point', { val })}">
            ${val}
          </div>
        `
          )
          .join('')}
      </div>
    `;

    return card;
  }

  private formatLogEntry(log: GameLogEntry): string {
    const params: Record<string, string | number> = {};
    if (log.params) {
      for (const [k, v] of Object.entries(log.params)) {
        if (typeof v === 'string' && v.startsWith('card_')) {
          params[k] = t(v as TranslationKey);
        } else {
          params[k] = v;
        }
      }
    }
    return t(log.messageKey, params);
  }

  private getStakesLabel(level: StakesLevel): string {
    switch (level) {
      case 'normal':
        return t('stakes_normal');
      case 'bit_it':
        return t('stakes_bit_it');
      case 'double_bit_it':
        return t('stakes_double_bit_it');
    }
  }

  private createEmotesBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'emotes-bar';

    const emotesList = document.createElement('div');
    emotesList.className = `emotes-list ${this.emotesOpen ? 'open' : ''}`;

    const emoteKeys = [
      'emote_hello',
      'emote_good_luck',
      'emote_bit_it',
      'emote_no_way',
      'emote_well_played',
      'emote_oops',
      'emote_haha',
      'emote_nice_try',
    ] as const;

    emoteKeys.forEach((key) => {
      const btn = document.createElement('button');
      btn.className = 'emote-item';
      btn.textContent = t(key);
      btn.onclick = () => {
        this.callbacks.onSendEmote(t(key));
        this.emotesOpen = false;
        emotesList.classList.remove('open');
      };
      emotesList.appendChild(btn);
    });

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'btn-icon';
    toggleBtn.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      <span>${t('slime_chat')}</span>
    `;
    toggleBtn.onclick = () => {
      this.emotesOpen = !this.emotesOpen;
      emotesList.classList.toggle('open', this.emotesOpen);
    };

    bar.appendChild(emotesList);
    bar.appendChild(toggleBtn);
    return bar;
  }

  public showRoundEndModal(state: GameState, onNextRound: () => void, _isOnline: boolean = false): void {
    const existing = document.getElementById('round-end-modal');
    if (existing) existing.remove();

    const backdrop = document.createElement('div');
    backdrop.id = 'round-end-modal';
    backdrop.className = 'modal-backdrop';

    const card = document.createElement('div');
    card.className = 'modal-card round-end-card';
    card.style.maxWidth = '460px';
    card.style.textAlign = 'center';

    const localPlayer = state.players.find((p) => p.id === this.localPlayerId);
    const roundWinner = state.players.find((p) => p.id === state.roundWinnerId);

    let isLocalWin = false;
    if (state.mode === 'teams' && roundWinner) {
      isLocalWin = localPlayer?.team === roundWinner.team;
    } else if (state.roundWinnerId) {
      isLocalWin = state.roundWinnerId === this.localPlayerId;
    }

    let winnerHeadline = '';
    let winnerSubline = '';
    if (state.roundWinnerId) {
      const winPoints = getStakesWinPoints(state.challenge.level, state.carriedOverBonus);
      const winnerName = state.mode === 'teams' && roundWinner
        ? t(roundWinner.team === 'A' ? 'team_a' : 'team_b')
        : roundWinner?.name || '';
      winnerHeadline = isLocalWin
        ? t('round_winner_you')
        : t('round_winner_other', { name: winnerName });
      winnerSubline = t('round_points_awarded', { points: winPoints });
    } else {
      winnerHeadline = t('round_tied');
      winnerSubline = '';
    }

    // Scores summary
    const scoresHtml = state.players
      .map((p) => {
        const bits = getBinaryBits(p.score);
        const bitValues = [1, 2, 4, 8];
        const isMe = p.id === this.localPlayerId;
        const displayName = isMe ? `${p.name} (${t('player_you')})` : p.name;
        return `
          <div class="round-score-row ${isMe ? 'highlight' : ''}">
            <span class="round-score-name">${displayName}</span>
            <div class="gems-row" style="margin: 0 10px;">
              ${bitValues
                .map(
                  (val, idx) => `
                <div class="gem-point ${bits[idx] ? 'lit' : ''}" style="width: 18px; height: 18px; font-size: 10px;">
                  ${val}
                </div>
              `
                )
                .join('')}
            </div>
            <span class="round-score-pts">${p.score} / 16</span>
          </div>
        `;
      })
      .join('');

    let countdownSec = 5;
    const nextBtnText = () =>
      countdownSec > 0 ? `${t('btn_next_round')} (${countdownSec}s)` : t('btn_next_round');

    card.innerHTML = `
      <div class="modal-body" style="padding: 28px 24px;">
        <span class="round-modal-badge">${t('round_num', { num: state.roundNumber })}</span>
        <h2 class="round-modal-title ${isLocalWin ? 'win' : ''}">${winnerHeadline}</h2>
        ${winnerSubline ? `<p style="font-size: 15px; font-weight: 700; color: #0284C7; margin: -10px 0 16px 0;">${winnerSubline}</p>` : ''}
        <div class="round-scores-container">
          ${scoresHtml}
        </div>
        <button class="btn-primary" id="btn-next-round" style="width: 100%; max-width: 260px; margin: 18px auto 0 auto; display: block;">
          ${nextBtnText()}
        </button>
      </div>
    `;

    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    let hasAdvanced = false;
    let timerId: number | null = null;

    const advance = () => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
      backdrop.remove();
      onNextRound();
    };

    const nextBtn = card.querySelector('#btn-next-round') as HTMLButtonElement | null;
    nextBtn?.addEventListener('click', () => {
      advance();
    });

    timerId = window.setInterval(() => {
      countdownSec -= 1;
      if (nextBtn) {
        nextBtn.textContent = nextBtnText();
      }
      if (countdownSec <= 0) {
        advance();
      }
    }, 1000);
  }

  public showMatchEndModal(state: GameState): void {
    const existing = document.getElementById('match-end-modal');
    if (existing) return;

    if (!this.hasCelebratedMatch) {
      this.hasCelebratedMatch = true;
      playMatchWin();
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
      });
    }

    const winner = state.players.find((p) =>
      state.mode === 'teams' ? p.team === state.matchWinnerId : p.id === state.matchWinnerId
    );

    const localPlayer = state.players.find((p) => p.id === this.localPlayerId);
    let isLocalWin = false;
    if (state.mode === 'teams' && winner) {
      isLocalWin = localPlayer?.team === winner.team;
    } else {
      isLocalWin = state.matchWinnerId === this.localPlayerId;
    }

    const winnerName =
      state.mode === 'teams' && winner?.team
        ? t(winner.team === 'A' ? 'team_a' : 'team_b')
        : winner?.name || t('player_champion');

    const backdrop = document.createElement('div');
    backdrop.id = 'match-end-modal';
    backdrop.className = 'modal-backdrop';

    const card = document.createElement('div');
    card.className = 'modal-card';
    card.style.textAlign = 'center';

    const scoresListHtml = state.players
      .map((p) => {
        const isMe = p.id === this.localPlayerId;
        const label = isMe ? `${p.name} (${t('player_you')})` : p.name;
        return `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 6px 12px; background: ${isMe ? '#F0F9FF' : '#FAFAFA'}; border-radius: 8px; font-size: 13px;">
            <span style="font-weight: 600; color: #27272A;">${label}</span>
            <span style="font-weight: 800; color: #0284C7;">${p.score} / 16</span>
          </div>
        `;
      })
      .join('');

    card.innerHTML = `
      <div class="modal-body" style="padding: 32px 24px;">
        <div style="display: flex; justify-content: center; margin-bottom: 12px;">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="${isLocalWin ? '#D97706' : '#71717A'}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
            <path d="M4 22h16"></path>
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
          </svg>
        </div>
        <h2 style="font-size: 26px; font-weight: 800; color: ${isLocalWin ? '#B45309' : '#27272A'}; margin-bottom: 6px;">
          ${isLocalWin ? t('match_victory') : t('match_defeat')}
        </h2>
        <p style="font-size: 14px; font-weight: 600; color: #52525B; margin-bottom: 18px;">
          ${t('event_match_won', { name: winnerName, points: winner?.score || 16 })}
        </p>

        <div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 22px; max-width: 320px; margin-left: auto; margin-right: auto;">
          <div style="font-size: 12px; font-weight: 700; color: #71717A; text-align: left; margin-bottom: 2px;">
            ${t('match_stats_final_score')} (${t('match_stats_rounds')}: ${state.roundNumber}):
          </div>
          ${scoresListHtml}
        </div>

        <div style="display: flex; gap: 12px; justify-content: center;">
          <button class="btn-primary" id="btn-again" style="max-width: 170px;">
            ${t('btn_play_again')}
          </button>
          <button class="btn-action btn-fold" id="btn-lobby" style="max-width: 170px;">
            ${t('btn_main_menu')}
          </button>
        </div>
      </div>
    `;

    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    card.querySelector('#btn-again')?.addEventListener('click', () => {
      backdrop.remove();
      this.callbacks.onPlayAgain();
    });

    card.querySelector('#btn-lobby')?.addEventListener('click', () => {
      backdrop.remove();
      this.callbacks.onBackToLobby();
    });
  }
}
